/**
 * Fase 2 do fluxo de auditoria e conciliação (spec 2026-09-11, §6).
 *
 * Aprovação automática: a política diz quem confere a folha contra o espelho e se o sistema
 * pode aprovar sozinho quando todos os critérios batem. Falhou um critério, o registro cai na
 * fila manual com o motivo escrito em `manualReviewReason` — e é esse motivo que a fila mostra.
 *
 * Arquivo separado de `ds-attendance.constants.ts` de propósito: é consumido pela tela de
 * política, pela fila e pela tela de auditoria (frentes de trabalho diferentes), e o arquivo
 * grande de constantes já passou de 400 linhas.
 */

/** Ligada só no valor explícito: `OFF` é o padrão de toda política nova (§6.1). */
export enum DSAttendanceAutoApproval {
  OFF = 'OFF',
  FULL_MATCH = 'FULL_MATCH'
}

/** Quem confere a folha física contra o espelho da escala (§6.2). */
export enum DSAttendanceSheetConferenceBy {
  AUDIT_TEAM = 'AUDIT_TEAM',
  UPLOADER = 'UPLOADER'
}

/** Como o registro foi aprovado. `null`/ausente = ainda não aprovado (ou backend antigo). */
export enum DSAttendanceApprovalMode {
  MANUAL = 'MANUAL',
  AUTOMATIC = 'AUTOMATIC'
}

/**
 * Motivos de revisão manual (§6.3). Os 6 primeiros são os critérios de aprovação automática
 * que falharam; `SAMPLED_FOR_REVIEW` é o sorteio de amostragem, que não é falha de nada.
 *
 * `DOCTOR_STATION_ALREADY_PAID` veio com o backend da fase 2: o plantão já foi pago em outro
 * registro, então o container não pode ser aprovado sozinho. `AUTO_APPROVAL_OFF` é reservado —
 * o backend o grava quando a política não liga a aprovação automática, e por isso ele NÃO é
 * opção de filtro (ver `MANUAL_REVIEW_REASON_OPTIONS`): filtraria quase a fila inteira.
 */
export enum DSAttendanceManualReviewReason {
  NO_SCHEDULE_EXPECTED = 'NO_SCHEDULE_EXPECTED',
  ROW_OUT_OF_TOLERANCE = 'ROW_OUT_OF_TOLERANCE',
  ROW_PENDING = 'ROW_PENDING',
  EXTRA_ROWS = 'EXTRA_ROWS',
  ABSENT_ROWS = 'ABSENT_ROWS',
  CONFERENCE_PENDING = 'CONFERENCE_PENDING',
  ILLEGIBLE_EVIDENCE = 'ILLEGIBLE_EVIDENCE',
  LATE_SUBMISSION = 'LATE_SUBMISSION',
  DOCTOR_STATION_ALREADY_PAID = 'DOCTOR_STATION_ALREADY_PAID',
  /**
   * Spec 2026-09-24 §6.4 — a aprovação automática encontrou conflito de perna num plantão
   * com pós-plantão aprovado: a sobrescrita é bloqueada e o registro vai para revisão manual.
   * `DOCTOR_STATION_ALREADY_PAID` segue no enum só por histórico (a trava dura acabou — Q1).
   */
  LEG_OVERWRITE_BLOCKED_FROZEN = 'LEG_OVERWRITE_BLOCKED_FROZEN',
  SAMPLED_FOR_REVIEW = 'SAMPLED_FOR_REVIEW',
  AUTO_APPROVAL_OFF = 'AUTO_APPROVAL_OFF'
}

/** Motivos que existem no contrato mas não viram opção do filtro "Motivo da revisão". */
const MANUAL_REVIEW_REASONS_NOT_FILTERABLE: readonly DSAttendanceManualReviewReason[] = [
  DSAttendanceManualReviewReason.AUTO_APPROVAL_OFF
]

/** Rótulo do chip de motivo na fila e no cabeçalho do registro. */
export const MANUAL_REVIEW_REASON_LABELS: Record<string, string> = {
  NO_SCHEDULE_EXPECTED: 'Sem escala prevista',
  ROW_OUT_OF_TOLERANCE: 'Horário fora da tolerância',
  ROW_PENDING: 'Linha pendente',
  EXTRA_ROWS: 'Linhas extras',
  ABSENT_ROWS: 'Linhas ausentes',
  CONFERENCE_PENDING: 'Conferência pendente',
  ILLEGIBLE_EVIDENCE: 'Evidência ilegível',
  LATE_SUBMISSION: 'Envio fora do prazo',
  DOCTOR_STATION_ALREADY_PAID: 'Plantão já pago em outro registro',
  LEG_OVERWRITE_BLOCKED_FROZEN: 'Sobrescrita bloqueada: pós-plantão aprovado',
  SAMPLED_FOR_REVIEW: 'Amostragem',
  AUTO_APPROVAL_OFF: 'Aprovação automática desligada'
}

/** Rótulo do estado de aprovação — o chip verde "Aprovado automaticamente" da fila. */
export const APPROVAL_MODE_LABELS: Record<string, string> = {
  MANUAL: 'Aprovado por auditoria',
  AUTOMATIC: 'Aprovado automaticamente'
}

/**
 * Motivo desconhecido não vira chip vazio nem quebra a fila: o backend pode ganhar um motivo
 * novo antes do deploy do web, e mostrar o código cru é melhor que esconder a informação.
 */
export function manualReviewReasonLabel(reason: string | null | undefined): string | null {
  if (!reason) return null
  return MANUAL_REVIEW_REASON_LABELS[reason] ?? reason
}

export function approvalModeLabel(mode: string | null | undefined): string | null {
  if (!mode) return null
  return APPROVAL_MODE_LABELS[mode] ?? mode
}

/** Opções do multi-select "Motivo da revisão" da fila, na ordem da tabela do spec. */
export const MANUAL_REVIEW_REASON_OPTIONS = Object.values(DSAttendanceManualReviewReason)
  .filter((value) => !MANUAL_REVIEW_REASONS_NOT_FILTERABLE.includes(value))
  .map((value) => ({ label: MANUAL_REVIEW_REASON_LABELS[value], value: value as string }))

/** Segmentado "Quem confere a folha contra o espelho" (mockup Eixo 3). */
export const SHEET_CONFERENCE_BY_OPTIONS = [
  { label: 'Time de auditoria', value: DSAttendanceSheetConferenceBy.AUDIT_TEAM },
  { label: 'Quem envia a folha', value: DSAttendanceSheetConferenceBy.UPLOADER }
]

/** Segmentado "Aprovação automática" (mockup Eixo 3). */
export const AUTO_APPROVAL_OPTIONS = [
  { label: 'Desligada', value: DSAttendanceAutoApproval.OFF },
  { label: 'Quando tudo bate', value: DSAttendanceAutoApproval.FULL_MATCH }
]

/** Os 6 critérios da §6.3, como o texto de ajuda do mockup os enumera. */
export const AUTO_APPROVAL_CRITERIA = [
  'escala prevista',
  'todas as linhas conciliadas dentro da tolerância',
  'nenhuma linha extra ou ausente',
  'conferência registrada',
  'evidência legível',
  'envio no prazo'
]

/**
 * Texto de ajuda da aprovação automática. A tolerância entra dentro do 2º critério porque é a
 * desta política — o critério central da §6.3 reusa exatamente esses dois números, e quem liga
 * a aprovação automática precisa ver qual folga está autorizando. Sem tolerância configurada
 * (modalidade que não a usa), o critério fica sem o parêntese em vez de mostrar "null min".
 */
export function buildAutoApprovalCriteriaText(tolerances: {
  checkinLateToleranceMinutes?: number | null
  checkoutEarlyToleranceMinutes?: number | null
}): string {
  const checkin = tolerances.checkinLateToleranceMinutes
  const checkout = tolerances.checkoutEarlyToleranceMinutes
  const hasTolerance =
    typeof checkin === 'number' &&
    Number.isFinite(checkin) &&
    typeof checkout === 'number' &&
    Number.isFinite(checkout)

  const criteria = AUTO_APPROVAL_CRITERIA.map((criterion, index) =>
    index === 1 && hasTolerance
      ? `${criterion} (entrada +${checkin} min, saída −${checkout} min desta política)`
      : criterion
  )

  return `Exige: ${criteria.join(', ')}.`
}

export const SHEET_CONFERENCE_BY_HELP =
  'Com "quem envia", a conferência acontece no upload e o auditor só recebe divergências.'

/**
 * O "dos aprovados automaticamente" vive aqui, e não no `suffix` do campo: como sufixo ele
 * ocupava a maior parte da largura do input e escondia o número digitado.
 */
export const MANUAL_REVIEW_SAMPLE_RATE_HELP =
  'Percentual dos registros aprovados automaticamente que ainda vai para revisão humana. ' +
  'Padrão 0. Use para manter uma trilha de controle quando ligar a aprovação automática numa ' +
  'escala nova.'

/** Padrões de política nova — repetidos na tela e no mapeamento da resposta. */
export const DEFAULT_SHEET_CONFERENCE_BY = DSAttendanceSheetConferenceBy.AUDIT_TEAM
export const DEFAULT_AUTO_APPROVAL = DSAttendanceAutoApproval.OFF
export const DEFAULT_MANUAL_REVIEW_SAMPLE_RATE = 0

/**
 * Campo de amostragem vazio = 0, não `NaN` nem `null`.
 *
 * `v-model.number` num `v-text-field` limpo devolve string vazia, e o yup casta isso para
 * `NaN`: sem normalizar, apagar o campo travava o salvamento no `typeError` ou mandava um
 * valor inválido para o backend. Apagar o número significa "não sortear nada".
 */
export function normalizeManualReviewSampleRate(raw: unknown): number {
  if (raw === null || raw === undefined || raw === '') return DEFAULT_MANUAL_REVIEW_SAMPLE_RATE
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : DEFAULT_MANUAL_REVIEW_SAMPLE_RATE
}

/** Ação da trilha que carrega o vetor de critérios avaliados (§6.4). */
export const AUTO_APPROVAL_CRITERIA_AUDIT_ACTION = 'CONTAINER_AUTO_APPROVED'

/**
 * Os seis critérios da §6.3 como frase completa — usados pelo modal "Ver critérios" quando a
 * trilha não devolve `newValues.criteria` (backend anterior ao vetor, ou container aprovado
 * antes da coluna). Versão longa de `AUTO_APPROVAL_CRITERIA`, que é a enumeração curta do
 * texto de ajuda da política.
 */
export const AUTO_APPROVAL_CRITERIA_FALLBACK: readonly string[] = [
  'A escala prevê ao menos um plantão com médico nesta data e turno.',
  'Toda linha prevista está conciliada, com entrada e saída dentro da tolerância da política.',
  'Nenhuma linha extra e nenhuma linha ausente.',
  'Conferência de assinatura e carimbo registrada (dispensada nas modalidades de aplicativo).',
  'Nenhuma evidência marcada como ilegível.',
  'Folha enviada dentro do prazo previsto.'
]

/** Nota exibida junto do texto estático, deixando claro que não é o vetor real avaliado. */
export const AUTO_APPROVAL_CRITERIA_FALLBACK_NOTE =
  'Critérios da política vigente. Este registro foi aprovado antes do detalhamento por critério, ' +
  'por isso a avaliação individual de cada item não está disponível.'
